import { round } from 'lodash'
import { BueroType, Values } from '../../../../types/Survey/Analysis/Calculation'
import { Concept } from '../../../../types/Survey/Analysis/Concept'
import { BueroTypeResult } from '../../../../types/Survey/Analysis/FactorValues'
import {
  PrebuildEinzelarbeitSubType,
  PrebuildMeetingGroesseBesprechungSubType,
  PrebuildMeetingGroesseCoworkingSubType,
  PrebuildType,
} from '../../../../types/Survey/Element'
import { getPercentage, roundUp } from '../../../../utility'
import completeAnalysisCalculation from './helper/completeAnalysisCalculation'
import { getEinzelbueros, getSonderbedarfe, getStaufleache, getWorkstations } from './helper/getAnswerValues'
import getHybridTemplate from './helper/getHybridTemplate'
import getQMAndDeskValues from './helper/getQMAndDeskValues'

const agil = (values: Values, concept?: Concept): BueroTypeResult => {
  const schreibtischePracticalPreRound = getPercentage(values.mitarbeiter, values.desksharingPercentage.percentage)

  const schreibtischeKlassischAndMoreThen =
    values.desksharingPercentage.percentage === 100 ? round(schreibtischePracticalPreRound) : roundUp(schreibtischePracticalPreRound)

  const sum =
    values.factor.einzelarbeitPercentage.KLASSISCHE_ARBEIT.value + values.factor.einzelarbeitPercentage.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.value
  const newKlassischeArbeitFactor = (values.factor.einzelarbeitPercentage.KLASSISCHE_ARBEIT.value / sum) * 100
  const newFokussierteArbeitMoreThenFactor = (values.factor.einzelarbeitPercentage.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.value / sum) * 100

  const klassischTheoreticalSpaceAmount = getPercentage(schreibtischeKlassischAndMoreThen, newKlassischeArbeitFactor)
  const klassischTheroeticalQM = klassischTheoreticalSpaceAmount * values.factor.einzelarbeitQM.KLASSISCHE_ARBEIT.value
  const klassischPracticalSpaceAmount = round(klassischTheroeticalQM / values.factor.einzelarbeitQM.KLASSISCHE_ARBEIT.value)
  const klassischPracticalQM = klassischPracticalSpaceAmount * values.factor.einzelarbeitQM.KLASSISCHE_ARBEIT.value

  const fokussiertMoreThenTheoreticalSpaceAmount = getPercentage(schreibtischeKlassischAndMoreThen, newFokussierteArbeitMoreThenFactor)
  const fokussiertMoreThenTheoreticalQM =
    fokussiertMoreThenTheoreticalSpaceAmount * values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.value
  const fokussiertMoreThenPracticalSpaceAmount = round(
    fokussiertMoreThenTheoreticalQM / values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
  )
  const fokussiertMoreThenPracticalQM = fokussiertMoreThenPracticalSpaceAmount * values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value

  let result: BueroTypeResult = {
    [PrebuildType.EINZELARBEIT]: {
      [PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT]: {
        theoreticalQM: klassischTheroeticalQM,
        theroeticalSpaceAmount: klassischTheoreticalSpaceAmount,
        practicalSpaceAmount: klassischPracticalSpaceAmount,
        practicalQM: klassischPracticalQM,
        arbeitsplatzAmount: klassischPracticalSpaceAmount * values.factor.arbeitsplatz.EINZELARBEIT.KLASSISCHE_ARBEIT.value,
      },
      [PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR]: {
        theoreticalQM: fokussiertMoreThenTheoreticalQM,
        theroeticalSpaceAmount: fokussiertMoreThenTheoreticalSpaceAmount,
        practicalSpaceAmount: fokussiertMoreThenPracticalSpaceAmount,
        practicalQM: fokussiertMoreThenPracticalQM,
        arbeitsplatzAmount: fokussiertMoreThenPracticalSpaceAmount * values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.value,
      },
      [PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.factor.arbeitstagPercentage.EINZELARBEIT.value),
            values.anwesenheitPercentage.percentage,
          ),
          values.factor.einzelarbeitPercentage.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
        ) * values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
        values.factor.einzelarbeitQM.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.value,
      ),
    },
    [PrebuildType.MEETING_GROESSE_BESPRECHUNG]: {
      [PrebuildMeetingGroesseBesprechungSubType.PERSON_2]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOMMUNIKATION.value,
          ),
          values.factor.meetingGroesseBesprechungPercentage.PERSON_2.value,
        ) * values.factor.meetingGroesseBesprechungQM.PERSON_2.value,
        values.factor.meetingGroesseBesprechungQM.PERSON_2.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_2.value,
      ),
      [PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOMMUNIKATION.value,
          ),
          values.factor.meetingGroesseBesprechungPercentage.PERSON_3_4.value,
        ) * values.factor.meetingGroesseBesprechungQM.PERSON_3_4.value,
        values.factor.meetingGroesseBesprechungQM.PERSON_3_4.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.value,
      ),
      [PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOMMUNIKATION.value,
          ),
          values.factor.meetingGroesseBesprechungPercentage.PERSON_5_8.value,
        ) * values.factor.meetingGroesseBesprechungQM.PERSON_5_8.value,
        values.factor.meetingGroesseBesprechungQM.PERSON_5_8.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.value,
      ),
      [PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOMMUNIKATION.value,
          ),
          values.factor.meetingGroesseBesprechungPercentage.PERSON_9_12.value,
        ) * values.factor.meetingGroesseBesprechungQM.PERSON_9_12.value,
        values.factor.meetingGroesseBesprechungQM.PERSON_9_12.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.value,
      ),
      [PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOMMUNIKATION.value,
          ),
          values.factor.meetingGroesseBesprechungPercentage.PERSON_13_16.value,
        ) * values.factor.meetingGroesseBesprechungQM.PERSON_13_16.value,
        values.factor.meetingGroesseBesprechungQM.PERSON_13_16.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.value,
      ),
    },
    [PrebuildType.MEETING_GROESSE_COWORKING]: {
      [PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOLLABORATION.value,
          ),
          values.factor.meetingGroesseCoworkingPercentage.PERSON_2_4.value,
        ) * values.factor.meetingGroesseCoworkingQM.PERSON_2_4.value,
        values.factor.meetingGroesseCoworkingQM.PERSON_2_4.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_2_4.value,
      ),
      [PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOLLABORATION.value,
          ),
          values.factor.meetingGroesseCoworkingPercentage.PERSON_5_8.value,
        ) * values.factor.meetingGroesseCoworkingQM.PERSON_5_8.value,
        values.factor.meetingGroesseCoworkingQM.PERSON_5_8.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_5_8.value,
      ),
      [PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOLLABORATION.value,
          ),
          values.factor.meetingGroesseCoworkingPercentage.PERSON_9_12.value,
        ) * values.factor.meetingGroesseCoworkingQM.PERSON_9_12.value,
        values.factor.meetingGroesseCoworkingQM.PERSON_9_12.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_9_12.value,
      ),
      [PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16]: getQMAndDeskValues(
        getPercentage(
          getPercentage(
            getPercentage(values.mitarbeiter, values.anwesenheitPercentage.percentage),
            values.factor.arbeitstagPercentage.KOLLABORATION.value,
          ),
          values.factor.meetingGroesseCoworkingPercentage.PERSON_13_16.value,
        ) * values.factor.meetingGroesseCoworkingQM.PERSON_13_16.value,
        values.factor.meetingGroesseCoworkingQM.PERSON_13_16.value,
        values.optimizationMode,
        values.factor.arbeitsplatz.MEETING_GROESSE_COWORKING.PERSON_13_16.value,
      ),
    },
    HYBRID: getHybridTemplate(),
    [PrebuildType.STAUFLAECHE]: getStaufleache(values),
    [PrebuildType.SONDERBEDARFE]: getSonderbedarfe(values),
    [PrebuildType.EINZELBUEROS]: getEinzelbueros(values),
    [PrebuildType.WORKSTATIONS]: getWorkstations(values),
  }

  return completeAnalysisCalculation(values, result, concept, BueroType.AGIL)
}

export default agil
